/**
 * FINISH COMPATIBILITY (2026-10-07, Matt: "I don't want to apply paint to a standing seam metal
 * roof to make it appear as copper … we shouldn't be able to apply copper to a wood finish. We
 * shouldn't be able to apply a composite decking finish to metal siding." Plan:
 * docs/bim-plan/23-project-style.md "Finish compatibility").
 *
 * Every finish material has a SUBSTRATE — what it's made of — and the substrate decides which
 * kinds of color it can wear. A finish can declare it (`MaterialRecord.substrate`, curated on the
 * firm library); otherwise it's inferred from the finish's own spec and name. A factory color is
 * tied to product LINES: on a finish that names its line (`MaterialRecord.productLine`) only that
 * line's colors fit; on a generic finish, any line made of the same substrate fits.
 *
 * Natural materials — copper, zinc, weathering / blackened / hot-rolled steel, slate, clay tile —
 * are finishes of their own and take no color: the material is the color.
 */
import type { ColorKind, ColorRecord } from './colors'

export type Substrate =
  | 'wood' | 'fiberCement' | 'stucco' | 'masonry' | 'gypsum' | 'concrete'
  | 'paintedMetal' | 'sheetMetal' | 'naturalMetal' | 'composite' | 'asphaltShingle' | 'clay' | 'stone' | 'pavers' | 'glass' | 'other'

export const SUBSTRATES: { value: Substrate; label: string; accepts: ColorKind[]; none?: string }[] = [
  { value: 'wood', label: 'Wood', accepts: ['paint', 'stain'] },
  { value: 'fiberCement', label: 'Fiber cement', accepts: ['paint', 'factory'] },
  { value: 'stucco', label: 'Stucco / plaster', accepts: ['integral', 'paint'] },
  { value: 'masonry', label: 'Brick / masonry', accepts: ['paint'] },
  { value: 'gypsum', label: 'Gypsum board', accepts: ['paint'] },
  { value: 'concrete', label: 'Concrete', accepts: ['paint', 'stain'] },
  { value: 'paintedMetal', label: 'Painted metal', accepts: ['coating', 'factory'] },
  // FINISH CONSOLIDATION (2026-10-07, Matt: standing seam "default to zinc, but we can change its
  // color/raw material with paint brush"): sheet metal that comes bare (zinc, copper, steel) or
  // factory-coated — one finish, its metal or coating picked with the Paint Brush
  { value: 'sheetMetal', label: 'Sheet metal (bare or coated)', accepts: ['bare', 'coating', 'factory'] },
  { value: 'naturalMetal', label: 'Natural metal', accepts: [], none: 'is a natural metal — the metal is its color' },
  { value: 'composite', label: 'Composite', accepts: ['factory'] },
  { value: 'asphaltShingle', label: 'Asphalt shingle', accepts: ['factory'] },
  { value: 'clay', label: 'Clay tile', accepts: [], none: 'shows its own fired clay color' },
  { value: 'stone', label: 'Natural stone / slate', accepts: [], none: 'is natural stone — the stone is its color' },
  // COBBLESTONE (2026-10-19): granite setts come in a stone color you pick — the cobblestone
  // line's own colors (cobblestone.ts), nothing painted on
  { value: 'pavers', label: 'Pavers / cobblestone', accepts: ['factory'] },
  { value: 'glass', label: 'Glass', accepts: [], none: 'takes no color' },
  { value: 'other', label: 'Other', accepts: ['paint', 'stain'] },
]
export const isSubstrate = (v: unknown): v is Substrate => SUBSTRATES.some((s) => s.value === v)
const subOf = (s: Substrate) => SUBSTRATES.find((x) => x.value === s)!

/** a manufacturer's product line — what its factory colors belong to */
export interface ProductLine { id: string; maker: string; name: string; substrate: Substrate }

export const PRODUCT_LINES: ProductLine[] = [
  { id: 'millboard-enhanced-grain', maker: 'Millboard', name: 'Enhanced Grain', substrate: 'composite' },
  { id: 'millboard-weathered-oak', maker: 'Millboard', name: 'Weathered Oak', substrate: 'composite' },
  { id: 'gaf-timberline-hdz', maker: 'GAF', name: 'Timberline HDZ', substrate: 'asphaltShingle' },
  { id: 'certainteed-presidential', maker: 'CertainTeed', name: 'Presidential Shake', substrate: 'asphaltShingle' },
  { id: 'granite-cobblestone', maker: 'Granite', name: 'Cobblestone', substrate: 'pavers' },
]
export const productLine = (id: unknown): ProductLine | null => PRODUCT_LINES.find((l) => l.id === id) ?? null
export const productLineLabel = (id: unknown): string => {
  const l = productLine(id)
  return l ? `${l.maker} ${l.name}` : String(id || '')
}

interface MatLike {
  name?: string
  substrate?: unknown
  productLine?: unknown
  roofing?: { profile?: string; shingle?: { style?: string } } | null
  cladding?: { profile?: string } | null
  floorPlank?: unknown
  floorFinish?: unknown
  ceilingFinish?: unknown
}

const NATURAL_METAL = /copper|zinc|weathering|corten|cor-ten|blackened|hot[- ]rolled|stainless|patina/i

/** what a finish is made of — declared, else read off its spec and name */
export function substrateOf(mat: MatLike | null | undefined): Substrate {
  if (!mat) return 'other'
  if (isSubstrate(mat.substrate)) return mat.substrate
  const name = mat.name || ''
  if (NATURAL_METAL.test(name)) return 'naturalMetal'
  const r = mat.roofing
  if (r) {
    if (r.profile === 'standingSeam') return 'sheetMetal'
    if (r.profile === 'barrel') return 'clay'
    if (r.profile === 'shingle') {
      const st = r.shingle?.style
      if (st === 'shake') return 'wood'
      if (st === 'slate') return 'stone'
      return 'asphaltShingle'
    }
    return /tile|clay/i.test(name) ? 'clay' : 'asphaltShingle'
  }
  const c = mat.cladding?.profile
  if (c === 'masonry') return /stone|ashlar|ledge/i.test(name) ? 'stone' : 'masonry'
  if (c === 'corrugated' || (c === 'vertical' && /metal/i.test(name))) return 'sheetMetal'
  if (/metal|alumin|steel/i.test(name)) return 'paintedMetal'
  if (/millboard|composite|trex|timbertech|azek/i.test(name)) return 'composite'
  if (/hardie|fiber ?cement/i.test(name)) return 'fiberCement'
  if (/stucco|plaster/i.test(name)) return 'stucco'
  if (/brick/i.test(name)) return 'masonry'
  if (/cobble/i.test(name)) return 'pavers'
  if (/marble|travertine|limestone|granite|slate|stone|terrazzo|tile/i.test(name)) return 'stone'
  if (/gyps|drywall/i.test(name) || mat.ceilingFinish) return 'gypsum'
  if (/concrete|cement/i.test(name)) return 'concrete'
  if (/glass/i.test(name)) return 'glass'
  if (c === 'lap' || c === 'boardBatten' || c === 'vertical' || /wood|cedar|oak|walnut|pine|fir|lumber|timber|parquet|veneer|shake/i.test(name)) return 'wood'
  if (mat.floorPlank) return 'composite'
  return 'other'
}

export interface Fit { ok: boolean; reason?: string }

/** can this finish wear this color? — the one rule every tool reads */
export function colorFits(mat: MatLike | null | undefined, color: Pick<ColorRecord, 'kind' | 'lines' | 'name'> | null | undefined): Fit {
  if (!color) return { ok: true }
  if (!mat) return { ok: true }   // no finish to judge (a built-in default look) — anything goes
  const name = mat.name || 'This finish'
  const sub = substrateOf(mat)
  const s = subOf(sub)
  if (!s.accepts.length) return { ok: false, reason: `${name} ${s.none || 'takes no color'}.` }
  if (!s.accepts.includes(color.kind)) {
    const takes = s.accepts.map((k) => ({ paint: 'paint', stain: 'stain', integral: 'integral color', coating: 'a metal coating (RAL)', factory: 'its maker’s colors', bare: 'a bare metal' }[k])).join(' or ')
    return { ok: false, reason: `${name} takes ${takes}, not ${kindNoun(color.kind)}.` }
  }
  if (color.kind === 'factory' && color.lines?.length) {
    const own = typeof mat.productLine === 'string' && mat.productLine ? mat.productLine : null
    if (own) {
      if (!color.lines.includes(own)) return { ok: false, reason: `${color.name} is a ${color.lines.map(productLineLabel).join(' / ')} color; ${name} is ${productLineLabel(own)}.` }
    } else if (!color.lines.some((l) => productLine(l)?.substrate === sub)) {
      return { ok: false, reason: `${color.name} is a ${color.lines.map(productLineLabel).join(' / ')} color; it doesn’t go on ${name}.` }
    }
  }
  return { ok: true }
}

function kindNoun(k: ColorKind): string {
  return { paint: 'paint', stain: 'stain', integral: 'integral stucco color', coating: 'a metal coating', factory: 'another maker’s color', bare: 'a bare metal' }[k]
}
